import { rd } from '@passionware/monads';
import type { ResolvedAppScope } from 'v5/core/app-scope/appScope.types';
import type {
  AnyFieldSpecBase,
  EditUnit
} from 'v5/core/form-schema/formSchema.types';
import type { UnitFoldSource } from 'v5/patterns/forms/schema-form/schemaForm.types';
import type { UserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';

/**
 * Field groups and lists (rates, locations) fold away, remembered per
 * browsing workspace, resource type and group (`<workspace>/<resource
 * type>/<group id | list field>`; the organization when browsing no
 * workspace). Teams share their workspace's: groups don't differ by team.
 */
export function fieldGroupFold(
  preferences: Pick<UserPreferencesService, 'forCollapsedFormGroup'>,
  scope: ResolvedAppScope,
  resourceTypeId: number
): UnitFoldSource {
  const workspace =
    scope.level === 'organization' ? 'organization' : String(scope.workspaceId);
  return {
    useFold(unit) {
      const group = foldKey(unit);
      // Called for every unit, so the hook order never changes.
      const store = preferences.forCollapsedFormGroup(
        `${workspace}/${resourceTypeId}/${group ?? 'none'}`
      );
      const collapsed = rd.tryGet(store.useValue()) ?? false;
      if (group === null) return null;
      return {
        collapsed,
        setCollapsed: next => void store.setValue(next)
      };
    }
  };
}

/** What a unit folds under, `null` when it doesn't fold. */
function foldKey(unit: EditUnit<AnyFieldSpecBase>): string | null {
  if (unit.container.type === 'custom-field-group') {
    return String(unit.container.definition.id);
  }
  // A list of a relation (rates, locations) folds under its field.
  return unit.type === 'list' ? (unit.item.fields[0]?.key ?? null) : null;
}
